import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignSaveMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignCreateJson from "./campaign-orgunits-create.json";

export const CampaignOrgUnitsCreateForm: FC<FormProps & { terminals1?: string[]; sites1?: string[] }> = ({
    runSubmit,
    terminals1,
    sites1,
}) => {
    const formDefinition = campaignCreateJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>();
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCampaignSaveMutation();

    // Get default values from form definition
    // and
    // Apply terminals1 and sites1 to formData if present
    useEffect(() => {
        if (formDefinition?.formConfiguration) {
            const initialValues: CampaignEntity = {} as CampaignEntity;

            formDefinition.formConfiguration.tabs.forEach((tab) => {
                tab.fields.forEach((field) => {
                    const defaultValue = field.attributes?.defaultValue;
                    if (defaultValue !== undefined) {
                        (initialValues as Record<string, unknown>)[field.name] = defaultValue;
                    }
                });
            });

            if (terminals1 !== undefined) {
                initialValues.terminals1 = {
                    terminalIdList: terminals1,
                    areAllTerminalsSelected: false,
                };
            }
            if (sites1 !== undefined) {
                initialValues.sites1 = sites1;
            }

            setFormData(initialValues as CampaignEntity);
        }
    }, [formDefinition, terminals1, sites1]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "Campaign",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && formData && (
                <FormManager<CampaignEntity>
                    name="CampaignCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
